/**
 * The Activities catalog: one typed entry per activity. This file (plus
 * runs.ts/runsSummary.ts) is the platform's ONLY public surface; everything
 * under lib/activities/<slug>/ and app/activities/<slug>/ is private to that
 * activity, enforced by ESLint (see README.md in this folder).
 *
 * Adding an activity = one entry here + its two folders + its own tables.
 * Destroying one = delete the entry and follow README.md's runbook.
 */

export type ActivitySlug =
  | "scenarios"
  | "teach-back"
  | "voice-capsule"
  | "phrase-chunks"
  | "false-cognates"
  | "conjugation-gym"
  | "collocations"
  | "delayed-recall"
  | "minimal-pairs";

/**
 * Target locales whose teach-back corpus has been hand-authored. Inlined rather
 * than read from the activity's content module: the registry must not reach
 * into activity internals (that is the whole point of the boundary). A
 * colocated test in the activity pins this set to the corpus, so the two cannot
 * drift silently.
 */
const TEACH_BACK_LOCALES = new Set(["pt-BR"]);

/** Locales with an authored anchor passage. Same boundary reasoning as above. */
const VOICE_CAPSULE_LOCALES = new Set(["pt-BR"]);

/** Locales with an authored chunk corpus. Same boundary reasoning as above. */
const PHRASE_CHUNK_LOCALES = new Set(["pt-BR"]);

/** Locales with an authored false-cognate corpus. Same boundary reasoning. */
const FALSE_COGNATE_LOCALES = new Set(["pt-BR"]);

/** Locales with hand-checked verb tables. Same boundary reasoning. */
const CONJUGATION_LOCALES = new Set(["pt-BR"]);

/** Locales with an authored collocation corpus. Same boundary reasoning. */
const COLLOCATION_LOCALES = new Set(["pt-BR"]);

/** Locales with an authored contrast corpus. Same boundary reasoning. */
const MINIMAL_PAIR_LOCALES = new Set(["pt-BR"]);

export type ActivityDefinition = {
  slug: ActivitySlug;
  /** i18n key; copy never lives inline. */
  titleKey: string;
  /** i18n key; copy never lives inline. */
  descriptionKey: string;
  /** Material Symbol name; must be in the app/layout.tsx preload list. */
  icon: string;
  href: string;
  /** "retired" = soft-kill: hidden from the catalog, code and data remain. */
  status: "active" | "retired";
  /** True when usable content exists for this target locale. */
  availableFor: (targetLocale: string) => Promise<boolean>;
};

export const ACTIVITIES: Record<ActivitySlug, ActivityDefinition> = {
  scenarios: {
    slug: "scenarios",
    titleKey: "activities.scenarios.title",
    descriptionKey: "activities.scenarios.description",
    icon: "forum",
    href: "/activities/scenarios",
    status: "active",
    // Scenario content is generated at runtime for any locale.
    availableFor: async () => true,
  },
  "teach-back": {
    slug: "teach-back",
    titleKey: "activities.teach-back.title",
    descriptionKey: "activities.teach-back.description",
    icon: "school",
    href: "/activities/teach-back",
    status: "active",
    availableFor: async (targetLocale) => TEACH_BACK_LOCALES.has(targetLocale),
  },
  "voice-capsule": {
    slug: "voice-capsule",
    titleKey: "activities.voice-capsule.title",
    descriptionKey: "activities.voice-capsule.description",
    icon: "mic",
    href: "/activities/voice-capsule",
    status: "active",
    availableFor: async (targetLocale) => VOICE_CAPSULE_LOCALES.has(targetLocale),
  },
  "phrase-chunks": {
    slug: "phrase-chunks",
    titleKey: "activities.phrase-chunks.title",
    descriptionKey: "activities.phrase-chunks.description",
    icon: "exercise",
    href: "/activities/phrase-chunks",
    status: "active",
    availableFor: async (targetLocale) => PHRASE_CHUNK_LOCALES.has(targetLocale),
  },
  "false-cognates": {
    slug: "false-cognates",
    titleKey: "activities.false-cognates.title",
    descriptionKey: "activities.false-cognates.description",
    icon: "psychology",
    href: "/activities/false-cognates",
    status: "active",
    availableFor: async (targetLocale) => FALSE_COGNATE_LOCALES.has(targetLocale),
  },
  "conjugation-gym": {
    slug: "conjugation-gym",
    titleKey: "activities.conjugation-gym.title",
    descriptionKey: "activities.conjugation-gym.description",
    icon: "exercise",
    href: "/activities/conjugation-gym",
    status: "active",
    availableFor: async (targetLocale) => CONJUGATION_LOCALES.has(targetLocale),
  },
  "minimal-pairs": {
    slug: "minimal-pairs",
    titleKey: "activities.minimal-pairs.title",
    descriptionKey: "activities.minimal-pairs.description",
    icon: "hearing",
    href: "/activities/minimal-pairs",
    status: "active",
    availableFor: async (targetLocale) => MINIMAL_PAIR_LOCALES.has(targetLocale),
  },
  "delayed-recall": {
    slug: "delayed-recall",
    titleKey: "activities.delayed-recall.title",
    descriptionKey: "activities.delayed-recall.description",
    icon: "visibility_off",
    href: "/activities/delayed-recall",
    status: "active",
    // No corpus: the passage is generated per session, so any target locale the
    // model writes works. Same reasoning as scenarios, and the reason this is
    // the only activity since scenarios with no *_LOCALES set above.
    availableFor: async () => true,
  },
  collocations: {
    slug: "collocations",
    titleKey: "activities.collocations.title",
    descriptionKey: "activities.collocations.description",
    icon: "assignment",
    href: "/activities/collocations",
    status: "active",
    availableFor: async (targetLocale) => COLLOCATION_LOCALES.has(targetLocale),
  },
};

export function activityHref(slug: ActivitySlug): string {
  return ACTIVITIES[slug].href;
}

/** Active + available entries, in registry (display) order. */
export async function activeActivities(targetLocale: string): Promise<ActivityDefinition[]> {
  const defs = Object.values(ACTIVITIES).filter((a) => a.status === "active");
  const availability = await Promise.all(defs.map((a) => a.availableFor(targetLocale)));
  return defs.filter((_, i) => availability[i]);
}
